import { motion } from 'framer-motion'
import { Lock, BookOpen, FileText, Video } from 'lucide-react'
import { CalendlyBtn } from '@/components/ui/calendly-btn'

const resourceTypes = [
  { icon: BookOpen, label: 'Reading Guides', description: 'Curated reading and reflection guides for IFS work.' },
  { icon: FileText, label: 'Worksheets', description: 'Practical exercises to use between coaching sessions.' },
  { icon: Video, label: 'Session Recordings', description: 'Access recordings from group sessions and workshops.' },
]

export default function ResourcesPage() {
  return (
    <div className="bg-white dark:bg-slate-950 min-h-screen">

      {/* Header */}
      <div className="bg-slate-900 pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="text-blue-400 text-sm font-semibold tracking-widest uppercase mb-3">
            Client Resources
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Your Resource Library
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="text-slate-400 text-lg max-w-2xl">
            A private space for active coaching clients. Access guides, worksheets, and session materials.
          </motion.p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">

        {/* Resource preview cards */}
        <div className="grid sm:grid-cols-3 gap-5 mb-12">
          {resourceTypes.map(({ icon: Icon, label, description }, i) => (
            <motion.div key={label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="p-6 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 text-center opacity-50">
              <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center mx-auto mb-4">
                <Icon size={18} className="text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white text-sm mb-2">{label}</h3>
              <p className="text-slate-500 dark:text-slate-400 text-xs leading-relaxed">{description}</p>
            </motion.div>
          ))}
        </div>

        {/* Login wall */}
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="bg-slate-900 rounded-3xl p-10 text-center">
          <div className="w-14 h-14 rounded-2xl bg-blue-600/20 flex items-center justify-center mx-auto mb-6">
            <Lock size={24} className="text-blue-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Client Access Required</h2>
          <p className="text-slate-400 leading-relaxed mb-8 max-w-md mx-auto">
            This area is exclusively for active coaching clients. Sign in with Google to access your personalized resource library.
          </p>
          <button
            disabled
            className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 text-white rounded-full text-sm font-medium cursor-not-allowed opacity-60 mb-3"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Sign in with Google
          </button>
          <p className="text-xs text-slate-600">Google OAuth integration — coming soon</p>
          <div className="border-t border-white/10 mt-8 pt-8">
            <p className="text-slate-400 text-sm mb-4">Not a client yet? Let's change that.</p>
            <CalendlyBtn size="sm" className="bg-blue-600 hover:bg-blue-700 text-white border-transparent rounded-full">
              Book Free Consultation
            </CalendlyBtn>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
